<template>
  <div class="meal-weighing-table">
    <div class="table-responsive">
      <table class="meal-weighing-table__table">
        <thead>
          <tr class="meal-weighing-table__bar">
            <th colspan="2" class="meal-weighing-table__meal-name">{{ meal.meal_name }}</th>
            <th v-for="column in calorieColumns" :key="column.calorieId" class="meal-weighing-table__number">
              {{ column.kcal }} {{ texts.KCAL_SUFFIX }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="group in summingGroups"
            :key="groupKeyOf(group)"
            class="meal-weighing-table__row"
            :class="{ 'meal-weighing-table__row--static': !canOpen(group) }"
            @click="openGroup(group)"
          >
            <td class="meal-weighing-table__group-name">{{ group.subgroup_name }}</td>
            <td class="meal-weighing-table__status">
              <group-status-cell :group="group" @open="openGroup(group)" />
            </td>
            <td v-for="column in calorieColumns" :key="column.calorieId" class="meal-weighing-table__number">
              <template v-if="isRawPortioned(group)">
                <template v-if="rawPortionCell(group, column.calorieId)">
                  {{ rawPortionCell(group, column.calorieId).unitsText }}
                  <span class="meal-weighing-table__sub">{{ rawPortionCell(group, column.calorieId).cookedText }}</span>
                </template>
                <template v-else>{{ texts.EMPTY_CELL }}</template>
              </template>
              <template v-else>{{ formatServingWeight(groupServingGrams(group, column.calorieId)) }}</template>
            </td>
          </tr>
          <tr
            v-for="group in zeroWeightGroups"
            :key="groupKeyOf(group)"
            class="meal-weighing-table__row meal-weighing-table__row--zero"
            @click="openGroup(group)"
          >
            <td>{{ group.subgroup_name }}</td>
            <td class="meal-weighing-table__status">
              <group-status-cell :group="group" @open="openGroup(group)" />
            </td>
            <td v-for="column in calorieColumns" :key="column.calorieId" class="meal-weighing-table__number">
              {{ texts.NOT_SUMMING_CELL }}
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="meal-weighing-table__total">
            <td colspan="2">
              {{ texts.MEAL_TOTAL_ROW }}<metric-info-tooltip :content="texts.MEAL_TOTAL_HELP" />
              <span v-if="pendingNote" class="meal-weighing-table__pending">{{ pendingNote }}</span>
            </td>
            <td v-for="column in calorieColumns" :key="column.calorieId" class="meal-weighing-table__number">
              <template v-if="hasTotalFor(column.calorieId)">
                <span class="meal-weighing-table__weighed">{{ formatServingWeight(mealTotalGrams(column.calorieId)) }}</span><span class="meal-weighing-table__recipe">{{ texts.MEAL_TOTAL_SEPARATOR }}{{ formatServingWeight(mealRecipeGrams(column.calorieId)) }}</span>
              </template>
              <template v-else>{{ texts.EMPTY_CELL }}</template>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
    <p v-if="zeroWeightNote" class="meal-weighing-table__zero-note">{{ zeroWeightNote }}</p>
    <group-weighing-dialog
      v-if="selectedGroup"
      :date="date"
      :group="selectedGroup"
      @close="selectedGroupKey = null"
      @group-updated="$emit('group-updated', $event)"
      @reload-requested="$emit('reload-requested')"
    />
  </div>
</template>

<script>
import GroupStatusCell from './GroupStatusCell.vue'
import GroupWeighingDialog from './GroupWeighingDialog.vue'
import MetricInfoTooltip from '@/components/Levels/MetricInfoTooltip'
import {
  buildCalorieColumns,
  getGroupServingGrams,
  getMealRecipeGrams,
  getMealTotalGrams,
  isRawPortionedGroup,
  isZeroWeightGroup,
  listPendingGroups
} from '@/utils/mealServingTotals'
import { describePendingGroups, describeZeroWeightGroups } from '../groupComposition'
import { canOpenGroup } from '../groupStatus'
import { describeRawPortionCell } from '../rawPortionDisplay'
import { formatServingGrams } from '../weightDisplay'
import { TEXTS } from '../cookedWeightsConstants'

export default {
  name: 'MealWeighingTable',
  components: {
    GroupStatusCell,
    GroupWeighingDialog,
    MetricInfoTooltip
  },
  props: {
    meal: {
      type: Object,
      required: true
    },
    date: {
      type: String,
      required: true
    },
    calories: {
      type: Object,
      required: true
    },
    recipeGramsByCalorie: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      selectedGroupKey: null
    }
  },
  computed: {
    texts () {
      return TEXTS
    },
    calorieColumns () {
      return buildCalorieColumns(this.calories)
    },
    summingGroups () {
      return this.meal.groups.filter((group) => !isZeroWeightGroup(group))
    },
    zeroWeightGroups () {
      return this.meal.groups.filter(isZeroWeightGroup)
    },
    zeroWeightNote () {
      return describeZeroWeightGroups(this.zeroWeightGroups)
    },
    pendingNote () {
      return describePendingGroups(listPendingGroups(this.meal.groups))
    },
    selectedGroup () {
      return this.meal.groups.find((group) => this.groupKeyOf(group) === this.selectedGroupKey) || null
    }
  },
  methods: {
    groupKeyOf (group) {
      return `${group.meal_id}-${group.subgroup_id}`
    },
    canOpen (group) {
      return canOpenGroup(group)
    },
    isRawPortioned (group) {
      return isRawPortionedGroup(group)
    },
    openGroup (group) {
      if (this.canOpen(group)) {
        this.selectedGroupKey = this.groupKeyOf(group)
      }
    },
    groupServingGrams (group, calorieId) {
      return getGroupServingGrams(group, calorieId)
    },
    rawPortionCell (group, calorieId) {
      return describeRawPortionCell(group, calorieId)
    },
    mealRecipeGrams (calorieId) {
      return getMealRecipeGrams(this.meal.groups, this.recipeGramsByCalorie, calorieId)
    },
    mealTotalGrams (calorieId) {
      return getMealTotalGrams(this.meal.groups, calorieId)
    },
    hasTotalFor (calorieId) {
      return this.mealTotalGrams(calorieId) !== null
    },
    formatServingWeight (grams) {
      return formatServingGrams(grams)
    }
  }
}
</script>

<style scoped>
.meal-weighing-table {
  margin-bottom: 32px;
}

.meal-weighing-table__table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  font-size: var(--font-base);
  font-variant-numeric: tabular-nums;
  background-color: var(--surface);
}

.meal-weighing-table__table th,
.meal-weighing-table__table td {
  padding: 8px 16px;
  border-bottom: 1px solid var(--line);
  vertical-align: middle;
}

.meal-weighing-table__bar th {
  height: var(--row-height);
  border-bottom: 0;
  font-size: var(--font-small);
  font-weight: 600;
  color: var(--text-inverse);
  text-transform: none;
  letter-spacing: normal;
  background-color: var(--brand-green);
}

.meal-weighing-table__bar .meal-weighing-table__meal-name {
  font-size: var(--font-base);
  font-weight: 700;
}

.meal-weighing-table__number {
  width: 96px;
  text-align: right;
  white-space: nowrap;
}

.meal-weighing-table__status {
  width: 280px;
}

.meal-weighing-table__row {
  height: var(--row-height);
  cursor: pointer;
}

.meal-weighing-table__row:nth-child(even) {
  background-color: var(--zebra);
}

.meal-weighing-table__row:not(.meal-weighing-table__row--static):hover {
  background-color: var(--brand-green-soft);
}

.meal-weighing-table__row--static {
  cursor: default;
}

.meal-weighing-table__sub {
  display: block;
  font-size: var(--font-small);
  font-weight: 400;
  color: var(--text-muted);
}

.meal-weighing-table__pending {
  display: block;
  margin-top: 6px;
  font-size: var(--font-small);
  font-weight: 600;
  color: var(--warning-text);
  white-space: normal;
}

.meal-weighing-table__group-name {
  min-width: 168px;
  font-weight: 700;
  color: var(--text-strong);
}

.meal-weighing-table__row--zero td {
  color: var(--text-faint);
}

.meal-weighing-table__total td {
  height: var(--row-height);
  font-weight: 700;
  color: var(--text-strong);
  background-color: var(--total-background);
}

.meal-weighing-table__total .meal-weighing-table__recipe {
  font-weight: 400;
  color: var(--text-muted);
}

.meal-weighing-table__zero-note {
  margin: 8px 0 0;
  font-size: var(--font-small);
  color: var(--text-muted);
}
</style>
